import { endOfDay, format, formatDistance, formatRelative, startOfWeek } from 'date-fns'
import React, { ReactElement, useState } from 'react'
import { RADOM_COLORS } from '../util/Constants'

interface IProps {
  date: Date
  textColor?: string
  formatModes?: TimeFormatType[]
}

export enum TimeFormatType {
  Relative,
  UTC,
  Distance
}

const getCustomRelativeDate = (date: Date): string => {
  const now = endOfDay(new Date())
  const daysDifference = (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)

  const localDate = new Date(date)
  const timeString = format(localDate, 'h:mm a')

  const startOfWeekDate = startOfWeek(now, { weekStartsOn: 1 })

  const startOfLastWeekDate = new Date(startOfWeekDate)
  startOfLastWeekDate.setDate(startOfWeekDate.getDate() - 7)

  if (daysDifference < -1) {
    return formatRelative(localDate, now)
  } else if (daysDifference < 0) {
    return `Tomorrow at ${timeString}`
  } else if (daysDifference < 1) {
    return `Today at ${timeString}`
  } else if (daysDifference < 2) {
    return `Yesterday at ${timeString}`
  } else if (date >= startOfWeekDate && daysDifference <= 0) {
    return `${format(localDate, 'EEEE')} at ${timeString}`
  } else if (date >= startOfLastWeekDate && date < startOfWeekDate) {
    return `Last ${format(localDate, 'EEEE')} at ${timeString}`
  }

  return formatRelative(localDate, now)
}

function TimeText({
  date,
  textColor,
  formatModes = [TimeFormatType.Relative, TimeFormatType.UTC]
}: IProps): ReactElement {
  const [timeFormatIndex, setTimeFormatIndex] = useState(0)
  const relativeDate = getCustomRelativeDate(date)

  return (
    <span
      style={{
        fontSize: 14,
        color: `${textColor ?? RADOM_COLORS.GRAY_DARKER}`,
        textDecoration: `underline dotted ${textColor ?? RADOM_COLORS.GRAY_DARK}`,
        textUnderlineOffset: '2px',
        cursor: 'pointer'
      }}
      onClick={e => {
        e.preventDefault()
        e.stopPropagation()
        setTimeFormatIndex((timeFormatIndex + 1) % formatModes.length)
      }}>
      {formatModes[timeFormatIndex] === TimeFormatType.Relative && relativeDate}
      {formatModes[timeFormatIndex] === TimeFormatType.Distance && formatDistance(date, new Date())}
      {formatModes[timeFormatIndex] === TimeFormatType.UTC && date.toUTCString()}
    </span>
  )
}

export default TimeText
